"use client";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styles from "./page.module.css";

export type PricePoint = {
  timestamp: string;
  value: number;
  isProvisional?: boolean;
};

type DynamicPriceChartProps = {
  points: PricePoint[];
  emptyMessage?: string;
};

type PriceTooltipPayload = {
  payload?: PricePoint;
};

const formatTime = (timestamp: string) =>
  new Date(timestamp).toLocaleTimeString("nl-BE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Brussels",
  });

const getBarColor = (value: number) => {
  if (value < 0) return "#22c55e";
  if (value < 45) return "#14b8a6";
  if (value < 90) return "#f59e0b";
  if (value < 140) return "#f97316";
  return "#ef4444";
};

function PriceTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: PriceTooltipPayload[];
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;

  return (
    <div className={styles.priceTooltip}>
      <span>Tijdstip: {formatTime(point.timestamp)}</span>
      <strong>€ {point.value.toFixed(2)}/MWh</strong>
      {point.isProvisional ? <small>Voorlopige waarde</small> : null}
    </div>
  );
}

export function DynamicPriceChart({ points, emptyMessage = "Geen kwartierprijzen beschikbaar voor deze dag." }: DynamicPriceChartProps) {
  const data = points.map((point, index) => ({
    ...point,
    tickIndex: index,
  }));

  if (data.length === 0) {
    return (
      <div className={styles.chartEmpty}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={styles.chartShell}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 20, right: 8, left: -18, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.09)" />
          <XAxis
            dataKey="timestamp"
            interval={0}
            minTickGap={4}
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#94a3b8", fontSize: 11 }}
            tickFormatter={(value, index) => (index % 8 === 0 ? formatTime(String(value)) : "")}
          />
          <YAxis
            width={56}
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#94a3b8", fontSize: 11 }}
            tickFormatter={(value) => `${value}`}
            label={{
              value: "EUR/MWh",
              angle: -90,
              position: "insideLeft",
              fill: "#64748b",
              fontSize: 11,
            }}
          />
          <ReferenceLine y={0} stroke="rgba(255,255,255,0.28)" />
          <Tooltip
            cursor={{ fill: "rgba(255,255,255,0.06)" }}
            content={<PriceTooltip />}
          />
          <Bar dataKey="value" radius={[3, 3, 0, 0]} isAnimationActive={false}>
            {data.map((entry) => (
              <Cell key={entry.timestamp} fill={getBarColor(entry.value)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
